import { ActivitySpinnerSize, ActivitySpinnerWidget, ButtonType, ButtonWidget, DialogType, InlineAlertType, InlineAlertWidget, ToggleSwitchWidget } from '@tableau/widgets';
import React from 'react';

import { ModalInstance, ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { ISiteCEPPStatusResult, SiteCEPPConfiguration, SiteCEPPConfigurationState } from '~src/settings/IntegrationsSettings/serverApi/types/IActivityLog';
import { SettingsStyles } from '~src/settings/styles/CommonStyles';

import { Messages } from '../../../codegen/Localize';
import WebHttpClient from '../../../httpClient/WebHttpClient';
import { getSessionManagementServiceInstance } from '../../../sessionManagement/SessionManagementService';
import { ActivityLogDeleteSiteRequest } from '../serverApi/requests/ActivityLogDeleteSiteRequest';
import { ActivityLogGetSiteStatusRequest } from '../serverApi/requests/ActivityLogGetSiteStatusRequest';
import { activityLogConnectionSetupSpacer, activityLogConnectionStatusPadding, activityLogSettingsDescription, activityLogSettingsGroupContainer, activityLogToggleButtonContainer } from '../styles/ActivityLogSettingsStyles';
import { integrationsSettingsPageHeader } from '../styles/IntegrationsSettingsStyles';
import { ActivityLogConnectionSetupDialog, ActivityLogConnectionSetupDialogBodyProps, ActivityLogConnectionSetupDialogWidgetProps } from './ActivityLogConnectionSetupDialog';
import { ActivityLogConnectionStatus } from './ActivityLogConnectionStatus';
import { ActivityLogSecurityFileVerification } from './ActivityLogSecurityFileVerification';

export const ACTIVITY_LOG = 'activity-log-platform';

const DEFAULT_SITE_CEPP_CONFIG = {
    awsAccountNumber: '',
    s3BucketName: '',
    kmsKeyARN: ''
};

const DEFAULT_SITE_CEPP_STATUS = {
    siteCEPPConfiguration: DEFAULT_SITE_CEPP_CONFIG,
    siteCEPPConfigurationState: SiteCEPPConfigurationState.NOT_ENABLED,
    error: ''
};

export interface ActivityLogSettingsProps {}

export function ActivityLogSettings(props: ActivityLogSettingsProps) {
    const [hasCEPPStatusLoaded, setCEPPStatusLoaded] = React.useState(false);
    const [isEnabled, setIsEnabled] = React.useState(false);
    const [siteCEPPConfig, setSiteCEPPConfig] = React.useState<SiteCEPPConfiguration>(DEFAULT_SITE_CEPP_CONFIG);
    const [siteCEPPConfigState, setSiteCEPPConfigState] = React.useState<SiteCEPPConfigurationState>(DEFAULT_SITE_CEPP_STATUS.siteCEPPConfigurationState);
    const [errorMsg, setErrorMsg] = React.useState('');

    const isActivityLogAvailableForSite = getSessionManagementServiceInstance().isActivityLogEnabled();

    // Leave dependency list empty to ensure effect only runs once, on first render
    React.useEffect(() => {
        WebHttpClient.sendRequest(new ActivityLogGetSiteStatusRequest()).then((result: ISiteCEPPStatusResult) => {
            if (result && result.siteCEPPConfiguration) {
                updateSiteCEPPStatus(result);

                // TODO (TFS1373415): Error message won't be localized if using direct server wording. Either map server response to Messages, or
                // use a catch-all error message in Messages
                if (result.siteCEPPConfigurationState === 'FAILED') { setErrorMsg(result.error); }
                setCEPPStatusLoaded(true);
            }
        });
    }, []);

    function updateSiteCEPPStatus(newStatus: ISiteCEPPStatusResult) {
        setSiteCEPPConfig(newStatus.siteCEPPConfiguration);
        setSiteCEPPConfigState(newStatus.siteCEPPConfigurationState);
        setErrorMsg(newStatus.error);
        setIsEnabled(newStatus.siteCEPPConfigurationState !== 'NOT_ENABLED' && newStatus.siteCEPPConfigurationState !== 'DEACTIVATED');
    }

    function deleteSite() {
        WebHttpClient.sendRequest(new ActivityLogDeleteSiteRequest()).then((result: ISiteCEPPStatusResult) => {
            // TODO (TFS1373415): Confirm error message to render; currently generic unexpected error
            const deactivatedStatus = {
                siteCEPPConfiguration: siteCEPPConfig,
                siteCEPPConfigurationState: SiteCEPPConfigurationState.DEACTIVATED,
                error: ''
            };
            if (result && result.error) {
                deactivatedStatus.error = result.error;
                deactivatedStatus.siteCEPPConfigurationState = result.siteCEPPConfigurationState ? result.siteCEPPConfigurationState : siteCEPPConfigState;
                deactivatedStatus.siteCEPPConfiguration = result.siteCEPPConfiguration ? result.siteCEPPConfiguration : siteCEPPConfig;
            }
            updateSiteCEPPStatus(deactivatedStatus);
        });
    }

    function popSetupDialog(props: ActivityLogConnectionSetupDialogWidgetProps) {
        let modalInstance: ModalInstance;

        const dialogProps: ActivityLogConnectionSetupDialogBodyProps = {
            configuration: siteCEPPConfig,
            // On cancel, make call to toggle back to off before closing dialog
            cancel: () => {
                props.toggleIsEnabled();
                modalInstance.close();
            },
            close: (result: any) => modalInstance && modalInstance.close(result),
            updateStatusCallback: updateSiteCEPPStatus
        };

        modalInstance = ReactModalService.open({
          type: DialogType.NormalWithTitleBar,
          title: Messages.setUpConnection(),
          dialogContent: React.createElement(ActivityLogConnectionSetupDialog, dialogProps),
          testId: `${ACTIVITY_LOG}-connection-setup-dialog-popup`,
          showCloseButton: false,
          shouldNotSetDefaultWidth: true,
          shouldNotCloseOnGlassClick: true,
          dialogWidth: TabDialogWidth.width375px,
        });

        return modalInstance.result;
    }

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        let newEnablementSetting = e.target.checked;

        if (newEnablementSetting) {
            // is site was not enabled, set the enablement state to true before opening the dialog
            // it will be updated to false if the actual state is NOT_ENABLED or DEACTIVATED after submitting the dialog
            setIsEnabled(newEnablementSetting);
            // if site was deactivated with error, clear the error message before submitting the dialog
            setErrorMsg('');
        }

        newEnablementSetting ? popSetupDialog({
            // Necessary for dialog to be able to callback to toggle
            toggleIsEnabled: () => setIsEnabled(!newEnablementSetting)
        }) : deleteSite();
    }

    const ToggleButton = (): JSX.Element => {
        return (
            <ToggleSwitchWidget textAlign='left' checked={isEnabled} onChange={handleChange} children={Messages.enable()} testId={`${ACTIVITY_LOG}-toggle-btn`}/>
        );
    };

    const EditConnectionButton = (): JSX.Element => {
        return (
            <ButtonWidget
                buttonType={ButtonType.Outline}
                onClick={() => popSetupDialog({
                    // Since connection is already enabled, canceling should only close the dialog, not affect enablement
                    toggleIsEnabled: () => {}
                })}
                testId={`${ACTIVITY_LOG}-edit-connection-btn`}>
                    {Messages.editConnection()}
            </ButtonWidget>
        );
    };

    const ActivityLogError = (errorMsg: string) => {
        return <div>
            <InlineAlertWidget
                children={errorMsg}
                type={InlineAlertType.Error}
                testId={`${ACTIVITY_LOG}-error`}
            />
        </div>;
    };

    const activityLogSettingsSection = (): JSX.Element => {
        return <>
            <div className={activityLogSettingsGroupContainer}>
                <h2 className={integrationsSettingsPageHeader}>{Messages.activityLog()}</h2>
                <div className={SettingsStyles.indent}>
                    <div className={activityLogSettingsDescription}>{Messages.activityLogDescription()}</div>
                    {hasCEPPStatusLoaded ?
                    <div>
                        <div className={activityLogToggleButtonContainer}>{ToggleButton()}</div>
                        <div className={activityLogConnectionStatusPadding}>
                            <div className={activityLogConnectionSetupSpacer}>
                                {(isEnabled && errorMsg) ? ActivityLogError(errorMsg) : null}
                            </div>
                            <div className={activityLogConnectionSetupSpacer}>
                                {(isEnabled && siteCEPPConfigState !== 'NOT_ENABLED' && siteCEPPConfigState !== 'DEACTIVATED') ?
                                    ActivityLogConnectionStatus({connection: {configuration: siteCEPPConfig, connectionStatus: siteCEPPConfigState}}
                                ) : null}
                            </div>
                            <div className={activityLogConnectionSetupSpacer}>
                                {(isEnabled && siteCEPPConfigState === 'PENDING_VERIFICATION') ? ActivityLogSecurityFileVerification({
                                    updateStatusCallback: updateSiteCEPPStatus
                                }) : null}
                            </div>
                            <div className={activityLogConnectionSetupSpacer}>
                                {(isEnabled && siteCEPPConfigState !== 'NOT_ENABLED' && siteCEPPConfigState !== 'DEACTIVATED') ? EditConnectionButton() : null}
                            </div>
                        </div>
                    </div>
                    :
                    <ActivitySpinnerWidget testId={`${ACTIVITY_LOG}-loading`} spinnerSize={ActivitySpinnerSize.Small} role={'status'} />
                    }
                </div>
            </div>
        </>;
    };

    return isActivityLogAvailableForSite ? activityLogSettingsSection() : null;
}
